// 🔧 src/utils/renderMessageHelper.js
// 🎯 ARQUITECTURA REUTILIZABLE PARA LAS 9 TARJETAS EPL
// Este helper mapea tipos visuales a componentes React específicos
// Reutilizable para: InnovaFast, InnovaTech, Transforma360 y los 6 laboratorios restantes

import React from 'react';
import { marked } from 'marked';
import * as LucideIcons from 'lucide-react';
import EPLRadioGroup from '../components/EPLRadioGroup';

// 🎨 COMPONENTES ESPECIALIZADOS POR TIPO VISUAL EPL
// Estos componentes son modulares y reutilizables entre todas las tarjetas

/**
 * 📝 NARRATIVA - Para introducciones y explicaciones
 * Usado en: Todos los labs para contextualización inicial
 * Reutilizable: ✅ Sí, para cualquier tarjeta EPL
 */
const NarrativaBlock = ({ titulo, cuerpo, opciones, onOptionClick, isProcessing, selectedValue }) => {
  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
      {titulo && (
        <h3 className="text-xl font-bold text-blue-900 mb-3 flex items-center gap-2">
          <LucideIcons.BookOpen className="w-5 h-5" />
          {titulo}
        </h3>
      )}
      
      <div className="prose prose-blue max-w-none text-gray-700 leading-relaxed">
        <div dangerouslySetInnerHTML={{ __html: marked.parse(cuerpo || "") }} />
      </div>
      
      {/* 🔄 SELECTOR INTELIGENTE - Radio vs Botones según contexto */}
      <EPLInteractionSelector 
        opciones={opciones} 
        onOptionClick={onOptionClick} 
        isProcessing={isProcessing}
        selectedValue={selectedValue}
        variant="primary"
      />
    </div>
  );
};

/**
 * 📊 FORMULARIO BREVE - Para recolección de input
 * Usado en: Definición de hipótesis, perfiles cliente, métricas
 * Reutilizable: ✅ Sí, con configuración de validaciones por tarjeta
 */
const FormularioBreveBlock = ({ 
  titulo, 
  cuerpo, 
  opciones, 
  onOptionClick, 
  onInputSubmit,
  isProcessing,
  selectedValue,
  placeholder = "Escribe tu respuesta aquí...",
  validaciones = {} 
}) => {
  const [inputValue, setInputValue] = React.useState('');
  const [error, setError] = React.useState('');

  // 🔍 VALIDACIONES REUTILIZABLES - Configurables por JSON
  const validateInput = (value) => {
    const { min_words = 0, max_chars = 500, required_terms = [] } = validaciones;
    
    if (min_words && value.split(' ').filter(w => w.length > 0).length < min_words) {
      return `Mínimo ${min_words} palabras requeridas`;
    }
    
    if (max_chars && value.length > max_chars) {
      return `Máximo ${max_chars} caracteres permitidos`;
    }
    
    if (required_terms.length > 0 && !required_terms.some(term => 
      value.toLowerCase().includes(term.toLowerCase())
    )) {
      return `Debe incluir uno de estos términos: ${required_terms.join(', ')}`;
    }
    
    return '';
  };

  const handleSubmit = () => {
    const validationError = validateInput(inputValue);
    if (validationError) {
      setError(validationError);
      return;
    }
    
    setError('');
    onInputSubmit && onInputSubmit(inputValue);
    setInputValue('');
  };

  return (
    <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6">
      {titulo && (
        <h3 className="text-xl font-bold text-green-900 mb-3 flex items-center gap-2">
          <LucideIcons.Edit3 className="w-5 h-5" />
          {titulo}
        </h3>
      )}
      
      <p className="text-gray-700 mb-4 leading-relaxed">{cuerpo}</p>
      
      {/* 📝 ÁREA DE INPUT REUTILIZABLE */}
      <div className="mb-4">
        <textarea
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder={placeholder}
          className={`w-full p-4 border-2 rounded-lg resize-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors ${
            error ? 'border-red-300 bg-red-50' : 'border-gray-300'
          }`}
          rows={4}
          disabled={isProcessing}
        />
        
        {error && (
          <p className="text-red-600 text-sm mt-2 flex items-center gap-1">
            <LucideIcons.AlertCircle className="w-4 h-4" />
            {error}
          </p>
        )}
        
        <div className="flex justify-between items-center mt-2">
          <span className="text-xs text-gray-500">
            {inputValue.split(' ').filter(w => w.length > 0).length} palabras
          </span>
          <span className="text-xs text-gray-500">
            {inputValue.length}/{validaciones.max_chars || 500}
          </span>
        </div>
      </div>
      
      {/* 🎯 BOTÓN DE ENVÍO + OPCIONES */}
      <div className="space-y-2">
        <button
          onClick={handleSubmit}
          disabled={!inputValue.trim() || isProcessing}
          className="w-full py-3 px-4 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          <LucideIcons.Send className="w-4 h-4" />
          Enviar Respuesta
        </button>
        
        <EPLInteractionSelector 
          opciones={opciones} 
          onOptionClick={onOptionClick} 
          isProcessing={isProcessing}
          selectedValue={selectedValue}
          variant="secondary"
        />
      </div>
    </div>
  );
};

/**
 * 💡 FEEDBACK ESTRATÉGICO - Para análisis y recomendaciones
 * Usado en: Análisis BML, feedback de entrevistas, decisiones pivot
 * Reutilizable: ✅ Sí, con diferentes esquemas de color por tarjeta
 */
const FeedbackEstrategicoBlock = ({ titulo, cuerpo, opciones, onOptionClick, isProcessing, selectedValue, variant = 'default' }) => {
  const variants = {
    default: 'from-purple-50 to-violet-50 border-purple-200 text-purple-900',
    success: 'from-green-50 to-emerald-50 border-green-200 text-green-900',
    warning: 'from-yellow-50 to-orange-50 border-yellow-200 text-yellow-900',
    info: 'from-blue-50 to-cyan-50 border-blue-200 text-blue-900'
  };

  return (
    <div className={`bg-gradient-to-br ${variants[variant]} border rounded-xl p-6`}>
      {titulo && (
        <h3 className="text-xl font-bold mb-3 flex items-center gap-2">
          <LucideIcons.Lightbulb className="w-5 h-5" />
          {titulo}
        </h3>
      )}
      
      <div className="prose max-w-none text-gray-700">
        <div dangerouslySetInnerHTML={{ __html: marked.parse(cuerpo || "") }} />
      </div>
      
      <EPLInteractionSelector 
        opciones={opciones} 
        onOptionClick={onOptionClick} 
        isProcessing={isProcessing}
        selectedValue={selectedValue}
        variant="success"
      />
    </div>
  );
};

/**
 * 📈 ANALÍTICA SIMPLE - Para métricas y simulaciones
 * Usado en: Resultados simulador, métricas MVP, análisis de riesgo
 * Reutilizable: ✅ Sí, con diferentes tipos de datos por tarjeta
 */
const AnaliticaSimpleBlock = ({ titulo, cuerpo, opciones, onOptionClick, isProcessing, selectedValue, metricas = [] }) => {
  return (
    <div className="bg-gradient-to-br from-cyan-50 to-blue-50 border border-cyan-200 rounded-xl p-6">
      {titulo && (
        <h3 className="text-xl font-bold text-cyan-900 mb-3 flex items-center gap-2">
          <LucideIcons.BarChart3 className="w-5 h-5" />
          {titulo}
        </h3>
      )}
      
      <p className="text-gray-700 mb-4">{cuerpo}</p>
      
      {/* 📊 VISUALIZACIÓN DE MÉTRICAS REUTILIZABLE */}
      {metricas.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {metricas.map((metrica, index) => (
            <div key={index} className="bg-white rounded-lg p-4 border">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-600">{metrica.nombre}</span>
                <span className="text-2xl font-bold text-cyan-600">{metrica.valor}</span>
              </div>
              {metrica.descripcion && (
                <p className="text-xs text-gray-500">{metrica.descripcion}</p>
              )}
            </div>
          ))}
        </div>
      )}
      
      <EPLInteractionSelector 
        opciones={opciones} 
        onOptionClick={onOptionClick} 
        isProcessing={isProcessing}
        selectedValue={selectedValue}
        variant="warning"
      />
    </div>
  );
};

/**
 * ⚖️ DECISIÓN ESTRATÉGICA - Para elecciones importantes
 * Usado en: Pivot/perseverar, selección formato MVP, decisiones críticas
 * Reutilizable: ✅ Sí, con diferentes contextos de decisión por tarjeta
 */
const DecisionEstrategicaBlock = ({ titulo, cuerpo, opciones, onOptionClick, isProcessing, selectedValue }) => {
  return (
    <div className="bg-gradient-to-br from-orange-50 to-red-50 border border-orange-200 rounded-xl p-6">
      {titulo && (
        <h3 className="text-xl font-bold text-orange-900 mb-3 flex items-center gap-2">
          <LucideIcons.GitBranch className="w-5 h-5" />
          {titulo}
        </h3>
      )}
      
      <p className="text-gray-700 mb-6 text-lg leading-relaxed">{cuerpo}</p>
      
      {/* 🎯 BOTONES DE DECISIÓN DESTACADOS */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {opciones?.map((opcion, index) => (
          <button
            key={index}
            onClick={() => onOptionClick(opcion)}
            disabled={isProcessing}
            className="flex items-center justify-center gap-3 p-4 border-2 border-orange-300 rounded-xl text-center font-medium text-orange-900 bg-white hover:bg-orange-50 hover:border-orange-400 transition-colors disabled:opacity-50"
          >
            <EPLIcon name={opcion.icono} className="w-5 h-5" />
            <span>{opcion.etiqueta}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

/**
 * 🏆 EVALUACIÓN RÚBRICA - Para resultados finales
 * Usado en: Demo Day, certificaciones, evaluaciones finales
 * Reutilizable: ✅ Sí, con diferentes criterios por tarjeta
 */
const EvaluacionRubricaBlock = ({ titulo, cuerpo, opciones, onOptionClick, isProcessing, selectedValue, rubrica = {} }) => {
  return (
    <div className="bg-gradient-to-br from-green-50 to-blue-50 border border-green-200 rounded-xl p-6">
      {titulo && (
        <h3 className="text-xl font-bold text-green-900 mb-3 flex items-center gap-2">
          <LucideIcons.Award className="w-5 h-5" />
          {titulo}
        </h3>
      )}
      
      <p className="text-gray-700 mb-6">{cuerpo}</p>
      
      {/* 📋 RÚBRICA VISUAL REUTILIZABLE */}
      {Object.keys(rubrica).length > 0 && (
        <div className="mb-6 space-y-4">
          <h4 className="font-semibold text-gray-900">Evaluación Detallada:</h4>
          {Object.entries(rubrica).map(([criterio, data], index) => (
            <div key={index} className="bg-white rounded-lg p-4 border">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-gray-800 capitalize">
                  {criterio.replace(/_/g, ' ')}
                </span>
                <span className="text-lg font-bold text-green-600">
                  {data.score}/{data.max}
                </span>
              </div>
              
              {/* 📊 BARRA DE PROGRESO */}
              <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                <div 
                  className="bg-green-500 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${(data.score / data.max) * 100}%` }}
                />
              </div>
              
              <p className="text-sm text-gray-600">{data.feedback}</p>
            </div>
          ))}
        </div>
      )}
      
      <EPLInteractionSelector 
        opciones={opciones} 
        onOptionClick={onOptionClick} 
        isProcessing={isProcessing}
        selectedValue={selectedValue}
        variant="accent"
      />
    </div>
  );
};

/**
 * 🎯 SELECTOR INTELIGENTE - Radio vs Botones
 * Detecta automáticamente si debe usar radio buttons o botones tradicionales
 * Radio: Para selección de estado + gating pedagógico
 * Botones: Para acciones inmediatas
 */
const EPLInteractionSelector = ({ opciones = [], onOptionClick, isProcessing, variant = 'default', selectedValue = null }) => {
  if (!opciones || opciones.length === 0) return null;

  // 🧠 DETECCIÓN INTELIGENTE: ¿Radio o Botones?
  const shouldUseRadioButtons = () => {
    // Usar radio buttons si hay opciones de avance + apoyo (gating context)
    const hasAdvanceOption = opciones.some(opt => 
      ['continuar', 'go_define', 'save_hypothesis'].includes(opt.valor)
    );
    const hasSupportOption = opciones.some(opt => 
      opt.valor.includes('example') || opt.valor.includes('ejemplo') || opt.valor.includes('help')
    );
    
    // Radio si hay tanto opciones de avance como de apoyo (contexto pedagógico)
    return hasAdvanceOption && hasSupportOption && opciones.length >= 2;
  };

  if (shouldUseRadioButtons()) {
    return (
      <div className="mt-4">
        <EPLRadioGroup
          opciones={opciones}
          selectedValue={selectedValue}
          onSelectionChange={onOptionClick}
          groupName={`epl-options-${Date.now()}`}
        />
      </div>
    );
  }

  // Usar botones tradicionales para acciones inmediatas
  return (
    <EPLButtonGroup 
      opciones={opciones} 
      onOptionClick={onOptionClick} 
      isProcessing={isProcessing}
      variant={variant}
    />
  );
};

/**
 * 🎯 BOTONES EPL CON JERARQUÍA Y ACCESIBILIDAD WCAG AA
 * Componente base para todas las opciones de interacción
 * ✅ Jerarquía automática: Primario vs Secundario
 * ✅ WCAG AA: Contraste 4.5:1, focus visible, keyboard navigation
 */
const EPLButtonGroup = ({ opciones = [], onOptionClick, isProcessing, variant = 'default' }) => {
  if (!opciones || opciones.length === 0) return null;

  // 🎨 JERARQUÍA AUTOMÁTICA: Detectar acciones principales
  const getPrimaryActions = () => [
    'guardar', 'save_hypothesis', 'enviar', 'continuar', 'go_define', 
    'confirmar', 'next', 'submit', 'finalizar'
  ];
  
  const getSecondaryActions = () => [
    'ver_ejemplo', 'show_example_intro', 'show_example_hypothesis', 
    'help', 'ayuda', 'cancelar', 'volver'
  ];

  const getButtonType = (valor) => {
    if (getPrimaryActions().some(action => valor.includes(action))) return 'primary';
    if (getSecondaryActions().some(action => valor.includes(action))) return 'secondary';
    return 'default';
  };

  // 🎨 VARIANTES CON CONTRASTE WCAG AA
  const variants = {
    primary: {
      base: 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700 focus:ring-blue-500',
      disabled: 'disabled:bg-gray-400 disabled:text-gray-200 disabled:border-gray-400'
    },
    secondary: {
      base: 'bg-white text-gray-700 border-gray-400 hover:bg-gray-50 focus:ring-gray-400',
      disabled: 'disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-200'
    },
    default: {
      base: 'bg-teal-50 text-teal-800 border-teal-400 hover:bg-teal-100 focus:ring-teal-400',
      disabled: 'disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-200'
    }
  };

  return (
    <div className="flex flex-col gap-3 mt-4" role="group" aria-label="Opciones de acción">
      {opciones.map((opcion, index) => {
        const buttonType = getButtonType(opcion.valor);
        const variantStyles = variants[buttonType] || variants.default;
        const isPrimary = buttonType === 'primary';
        
        return (
          <button
            key={index}
            onClick={() => onOptionClick(opcion)}
            disabled={isProcessing}
            type="button"
            className={`
              flex items-center gap-3 px-4 py-3 border-2 rounded-lg text-left font-medium 
              transition-all duration-200 disabled:cursor-not-allowed
              focus:outline-none focus:ring-4 focus:ring-opacity-50
              ${variantStyles.base} ${variantStyles.disabled}
              ${isPrimary ? 'font-semibold shadow-sm' : ''}
            `}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                if (!isProcessing) onOptionClick(opcion);
              }
            }}
          >
            <EPLIcon 
              name={opcion.icono} 
              className="w-4 h-4 flex-shrink-0" 
              aria-hidden="true"
            />
            <span className="flex-1">
              {opcion.etiqueta}
              {isPrimary && (
                <span className="ml-2 text-xs opacity-75">(Recomendado)</span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
};

/**
 * 🎯 ICONO EPL REUTILIZABLE
 * Componente para iconos consistentes en todas las tarjetas
 */
const EPLIcon = ({ name, className = "w-4 h-4", ...props }) => {
  const LucideIcon = LucideIcons[name] || LucideIcons.ChevronRight;
  return <LucideIcon className={className} {...props} />;
};

// 🗺️ MAPA DE TIPOS VISUALES - NÚCLEO REUTILIZABLE
// Este objeto mapea cada tipo_visual a su componente correspondiente
// Extensible para las 9 tarjetas EPL
export const EPL_VISUAL_TYPES = {
  // 📝 Tipos básicos (reutilizables en todas las tarjetas)
  'narrativa': NarrativaBlock,
  'formulario_breve': FormularioBreveBlock,
  'feedback_estrategico': FeedbackEstrategicoBlock,
  'analitica_simple': AnaliticaSimpleBlock,
  'decision_estrategica': DecisionEstrategicaBlock,
  'evaluacion_rubrica': EvaluacionRubricaBlock,
  
  // 🔄 Alias para compatibilidad con sistema existente
  'feedback_basico': FeedbackEstrategicoBlock,
  'feedback_diagnostico': FeedbackEstrategicoBlock,
  
  // 🚀 Extensiones futuras para otras tarjetas EPL
  // Estas se pueden agregar sin modificar el código base
  'simulacion_crisis': AnaliticaSimpleBlock,      // Para Crisis Management
  'dinamica_equipo': DecisionEstrategicaBlock,    // Para Team Dynamics
  'pitch_presentation': EvaluacionRubricaBlock,   // Para presentaciones
  'risk_analysis': AnaliticaSimpleBlock,          // Para análisis de riesgo
  'strategic_planning': FormularioBreveBlock      // Para planificación estratégica
};

/**
 * 🎯 FUNCIÓN PRINCIPAL DE RENDERIZADO
 * Esta función es el corazón del sistema reutilizable
 * Recibe un mensaje y devuelve el componente correcto
 */
export const renderEPLMessage = (message, onOptionClick, onInputSubmit, isProcessing = false, selectedValue = null) => {
  const { tipo_visual, mensaje_coach, opciones, rubrica, metricas } = message;
  
  // 🔍 Buscar el componente correspondiente
  const Component = EPL_VISUAL_TYPES[tipo_visual];
  
  if (!Component) {
    console.warn(`[EPL] Tipo visual no reconocido: ${tipo_visual}`);
    return null;
  }
  
  // 📦 Preparar props comunes para todos los componentes
  const commonProps = {
    titulo: mensaje_coach?.titulo || message.titulo,
    cuerpo: mensaje_coach?.cuerpo || message.content,
    opciones: opciones || [],
    onOptionClick,
    onInputSubmit,
    selectedValue: selectedValue || null, // 🔒 Null safety
    isProcessing,
    rubrica: rubrica || {},
    metricas: metricas || []
  };
  
  return <Component {...commonProps} />;
};

// 🔄 COMPATIBILIDAD CON SISTEMA EXISTENTE
export function renderMensaje(message, index) {
  if (message?.tipo_visual === "bienvenida") {
    // Importar dinámicamente para evitar dependencias circulares
    const WelcomeMenuBubble = require("../components/WelcomeMenuBubble").default;
    return <WelcomeMenuBubble key={index} message={message} />;
  }
  
  // Usar MessageBubble para tipos no EPL
  const MessageBubble = require("../components/MessageBubble").default;
  return <MessageBubble key={index} message={message} />;
}

export default {
  renderEPLMessage,
  EPL_VISUAL_TYPES,
  EPLIcon,
  EPLButtonGroup,
  EPLInteractionSelector
};
